import type {QuickSearch} from '@src/stores/types.ts';
import type {QuickSearchBody} from '@src/types/quickSearch.ts';
import type {
  AssetFilter,
  DateRangeFilter,
  RangeFilter,
  StringInFilter,
} from '@src/types/searchFilters.ts';
import type {Feature} from 'geojson';
import type {Extent} from 'ol/extent';
import {getFeatureByReference} from '@apps-common/geocorn-sdk/api/features.js';
import {
  PSSCENE_IMAGERY_CAPABILITIES_ASSETS,
  SR_ASSETS,
} from '@src/constants/assetTypes.ts';
import {
  CONFIGS,
  findNestedObj,
  getDateRangesFromFilters,
  getGeometry,
  KEYS,
} from '@src/utils/filters.ts';
import bbox from '@turf/bbox';
import isEqual from 'lodash/isEqual.js';

export type ParsedSearch = QuickSearch & {
  feature: Feature | null;
  extent: Extent | null;
};

export const parseSavedSearch = async (savedSearch: QuickSearchBody) => {
  const parsedSearch: ParsedSearch = {
    dailyEmailEnabled: savedSearch.__daily_email_enabled,
    id: savedSearch.id,
    name: savedSearch.name,
    itemTypes: savedSearch.item_types,
    dateRanges: [],
    filters: {},
    feature: null,
    extent: null,
  };

  const searchFilters = savedSearch.filter;

  const dateRangeFilters = findNestedObj(
    searchFilters,
    'type',
    'DateRangeFilter',
  ) as DateRangeFilter[];

  parsedSearch.dateRanges = getDateRangesFromFilters(dateRangeFilters);

  const rangeFilters = findNestedObj(
    searchFilters,
    'type',
    'RangeFilter',
  ) as RangeFilter[];

  rangeFilters.forEach(rangeFilter => {
    const filterConfig = CONFIGS[rangeFilter.field_name!];
    const configRange = filterConfig.range!;
    if (filterConfig) {
      const gtVal =
        rangeFilter.config.gte || rangeFilter.config.gt || configRange[0];
      const ltVal =
        rangeFilter.config.lte || rangeFilter.config.lt || configRange[1];
      const values: [number, number] = [gtVal, ltVal];

      parsedSearch.filters[rangeFilter.field_name!] = values;
    }
  });

  const groundControl = findNestedObj(
    searchFilters,
    'field_name',
    'ground_control',
  )[0];

  if (groundControl) {
    parsedSearch.filters[KEYS.GROUND_CONTROL] = !!groundControl;
  }

  const imageQuality = findNestedObj(
    searchFilters,
    'field_name',
    'quality_category',
  )[0];
  if (imageQuality) {
    parsedSearch.filters[KEYS.QUALITY_CATEGORY] = !!imageQuality;
  }

  const permissionFilter = findNestedObj(
    searchFilters,
    'type',
    'PermissionFilter',
  )[0];
  if (permissionFilter) {
    parsedSearch.filters.permissionFilter = !!permissionFilter;
  }

  const instrumentFilter = findNestedObj(
    searchFilters,
    'field_name',
    'instrument',
  )[0] as StringInFilter;

  if (instrumentFilter) {
    parsedSearch.filters.instruments = instrumentFilter.config.reduce(
      (acc: Record<string, boolean>, curr) => ((acc[curr] = true), acc),
      {},
    );
  }

  const publishingStageFilter = findNestedObj(
    searchFilters,
    'field_name',
    'publishing_stage',
  )[0] as StringInFilter;

  if (publishingStageFilter) {
    parsedSearch.filters.publishingStages = publishingStageFilter.config.reduce(
      (acc: Record<string, boolean>, curr) => ((acc[curr] = true), acc),
      {},
    );
  }

  const satelliteIdFilter = findNestedObj(
    searchFilters,
    'field_name',
    'satellite_id',
  )[0] as StringInFilter;
  if (satelliteIdFilter) {
    parsedSearch.filters[KEYS.SATELLITE_ID] = satelliteIdFilter?.config;
  }

  const assetFilters = findNestedObj(
    searchFilters,
    'type',
    'AssetFilter',
  ) as AssetFilter[];

  const SRFilter = !!assetFilters.find(filter =>
    isEqual(filter.config, SR_ASSETS),
  );

  if (SRFilter) {
    parsedSearch.filters[KEYS.ONLY_SR] = SRFilter;
  }

  const imageryCaps = Object.keys(PSSCENE_IMAGERY_CAPABILITIES_ASSETS).filter(
    cap => {
      return assetFilters.some((filter: AssetFilter) => {
        const config = filter.config;

        return config.includes(PSSCENE_IMAGERY_CAPABILITIES_ASSETS[cap]);
      });
    },
  );

  if (imageryCaps.length > 0) {
    parsedSearch.filters.imageryCapabilities = imageryCaps;
  }

  let mapFeature = {
    type: 'Feature',
    geometry: savedSearch.geometry ?? getGeometry(searchFilters),
    properties: {},
    id: savedSearch.id,
  } as Feature;

  savedSearch.geometry;
  if (savedSearch.geometry?.type === 'ref') {
    mapFeature = await getFeatureByReference(savedSearch.geometry.content);
  }

  parsedSearch.feature = mapFeature;
  parsedSearch.extent = bbox(mapFeature.geometry);

  return parsedSearch;
};
